import React, { useEffect, useState } from 'react';
import packageJson from '../package.json';

global.appVersion = packageJson.version;

// Compare two semantic versions
const semverGreaterThan = (versionA, versionB) => {
  const versionsA = versionA.split('.').map(Number);
  const versionsB = versionB.split('.').map(Number);
  const maxLen = Math.max(versionsA.length, versionsB.length);

  for (let i = 0; i < maxLen; i++) {
    const a = versionsA[i] || 0;
    const b = versionsB[i] || 0;
    if (a > b) return true;
    if (a < b) return false;
  }
  return false;
};

const CacheBuster = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isLatestVersion, setIsLatestVersion] = useState(true);

  const refreshCacheAndReload = async (latestVersion) => {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('Service worker caches cleared');
      }
    } catch (error) {
      console.warn('Cache clear failed', error);
    }

    // Store acknowledged version to prevent loop
    localStorage.setItem('acknowledgedVersion', latestVersion);

    // Force reload with query param
    window.location.href = `${window.location.origin}${window.location.pathname}?reload=${Date.now()}`;
  };

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch(`/meta.json?t=${Date.now()}`, {
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });
        const meta = await res.json();
        const latestVersion = meta.version;
        const currentVersion = global.appVersion;
        const acknowledgedVersion = localStorage.getItem('acknowledgedVersion');

        const isNewVersion =
          semverGreaterThan(latestVersion, currentVersion) &&
          latestVersion !== acknowledgedVersion;

        if (isNewVersion) {
          console.log(`New version found: ${latestVersion}`);
          setIsLatestVersion(false);
        } else {
          console.log(`App is up-to-date or already acknowledged.`);
          setIsLatestVersion(true);
        }
      } catch (error) {
        console.error('Could not fetch version metadata', error);
        setIsLatestVersion(true);
      } finally {
        setLoading(false);
      }
    };

    checkVersion();
  }, []);

  useEffect(() => {
    if (!loading && !isLatestVersion) {
      if (
        window.confirm('A new version of the app is available. Reload to update?')
      ) {
        fetch(`/meta.json?t=${Date.now()}`)
          .then(res => res.json())
          .then(meta => refreshCacheAndReload(meta.version))
          .catch(() => {
            // fallback in case meta fails again
            refreshCacheAndReload(global.appVersion);
          });
      }
    }
  }, [loading, isLatestVersion]);

  if (loading) return <div>Checking for updates...</div>;

  return children({ loading, isLatestVersion, refreshCacheAndReload });
};

export default CacheBuster;
